import clsx from 'clsx'
import { LogOut, X } from 'lucide-react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { navItems } from '../../app/navigation'
import { Button } from '../ui/Button'
import { setSidebarOpen } from '../../store/adminSlice'
import { clearAuth } from '../../store/authSlice'
import { api } from '../../lib/api'
import type { AppDispatch, RootState } from '../../store'

export function Sidebar() {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [confirmLogout, setConfirmLogout] = useState(false)
  const sidebarOpen = useSelector((root: RootState) => root.admin.sidebarOpen)

  return (
    <>
      <button
        className={clsx('fixed inset-0 z-30 bg-stone-950/30 lg:hidden', sidebarOpen ? 'block' : 'hidden')}
        onClick={() => dispatch(setSidebarOpen(false))}
        type="button"
      />
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-72 -translate-x-full flex-col border-r border-stone-200 bg-white px-4 py-5 transition lg:sticky lg:top-0 lg:h-svh lg:translate-x-0',
          sidebarOpen && 'translate-x-0',
        )}
      >
        <div className="flex items-center justify-between">
          <NavLink className="flex items-center gap-3" to="/dashboard">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-mango-yellow/30 font-bold text-leaf-dark">S</div>
            <div>
              <p className="text-lg font-bold text-leaf-dark">Seasonly</p>
              <p className="text-xs font-medium text-stone-500">Mango Admin</p>
            </div>
          </NavLink>
          <button
            className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 lg:hidden"
            onClick={() => dispatch(setSidebarOpen(false))}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-8 space-y-1">
          {navItems.map((item) => {
            const IconNode = item.icon
            return (
              <NavLink
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold transition',
                    isActive
                      ? 'bg-mango-orange text-white shadow-sm'
                      : 'text-stone-600 hover:bg-mango-yellow/20 hover:text-leaf-dark',
                  )
                }
                key={item.to}
                onClick={() => dispatch(setSidebarOpen(false))}
                to={item.to}
              >
                <IconNode className="h-5 w-5" />
                <span className="min-w-0 flex-1">{item.label}</span>
              </NavLink>
            )
          })}
        </nav>

        <button
          className="mt-auto flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-3 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-50"
          onClick={() => setConfirmLogout(true)}
          type="button"
        >
          <LogOut className="h-5 w-5" />
          Logout
        </button>
      </aside>
      {confirmLogout ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-stone-950/40 p-4">
          <div className="w-full max-w-sm rounded-xl border border-stone-200 bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-stone-950">Confirm logout</h3>
                <p className="mt-1 text-sm leading-6 text-stone-600">You will be returned to the admin login screen.</p>
              </div>
              <button className="rounded-lg p-1 text-stone-400 hover:bg-stone-100" onClick={() => setConfirmLogout(false)} type="button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setConfirmLogout(false)} variant="ghost">Cancel</Button>
              <Button onClick={async () => { try { await api.post('/admin/auth/logout') } catch { void 0 } dispatch(clearAuth()); navigate('/login') }} variant="danger">Logout</Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
